import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import Modal from 'react-modal';

import { DuplicateBookingData } from '../types/duplicateBooking';
import { getHotelName, getHotelLocation } from '../utilities/hotelHelpers';

import { PrimaryButton, SecondaryButton } from './Button';

// Set the app element for accessibility
if (typeof window !== 'undefined') {
  Modal.setAppElement('#root');
}

interface DuplicateBookingConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  duplicateBookings: DuplicateBookingData[];
}

const DuplicateBookingConfirmationModal: React.FC<
  DuplicateBookingConfirmationModalProps
> = ({ isOpen, onClose, onConfirm, duplicateBookings }) => {
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset acknowledged state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setAcknowledged(false);
    }
  }, [isOpen]);

  // Flatten all overlapping bookings
  const allOverlappingBookings = duplicateBookings.flatMap(
    (data) => data.overlappingBookings || [],
  );

  const handleConfirm = () => {
    if (acknowledged) {
      onConfirm();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="relative mx-auto mt-20 max-w-2xl rounded-lg bg-white p-6 shadow-xl"
      overlayClassName="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 px-4 overflow-y-auto"
      closeTimeoutMS={200}
    >
      <div className="mb-4 flex items-start">
        <ExclamationTriangleIcon className="mr-3 size-6 shrink-0 text-yellow-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Duplicate Booking Warning
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            We found existing bookings for one or more guests that overlap with
            the dates you selected.
          </p>
        </div>
      </div>

      <div className="mb-4 max-h-64 overflow-y-auto rounded-md border border-yellow-200 bg-yellow-50 p-4">
        <h3 className="mb-2 font-medium text-yellow-800">
          Overlapping Bookings:
        </h3>
        <ul className="space-y-2 text-sm">
          {allOverlappingBookings.map((booking) => (
            <li key={booking.id} className="border-b border-yellow-200 pb-2">
              <div className="text-yellow-800">
                <span className="font-semibold">
                  {format(parseISO(booking.checkInDate), 'MMM d, yyyy')}
                  {' - '}
                  {format(parseISO(booking.checkOutDate), 'MMM d, yyyy')}
                </span>
              </div>
              <div className="text-yellow-700">
                {getHotelName(booking.hotel)} ({getHotelLocation(booking.hotel)}
                )
                {booking.hotelRoomNumber && (
                  <span className="ml-1">- Room {booking.hotelRoomNumber}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-6 rounded-md border border-gray-200 bg-gray-50 p-4">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mr-3 mt-0.5 size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            I acknowledge that there are existing bookings for these dates and I
            authorize this booking to proceed. I understand that this may result
            in duplicate reservations.
          </span>
        </label>
      </div>

      <div className="flex gap-3">
        <SecondaryButton
          text="Cancel"
          onClick={onClose}
          type="button"
          className="flex-1"
        />
        <PrimaryButton
          text="Confirm Booking"
          onClick={handleConfirm}
          disabled={!acknowledged}
          className="flex-1"
        />
      </div>
    </Modal>
  );
};

export default DuplicateBookingConfirmationModal;
